/**
 * Represents the state of a form input.
 *
 * This type is used to describe the current value of a form input field and
 * any associated validation errors.
 *
 * Properties:
 * - `value`: The current value of the input field. It can be a string or a boolean,
 *   depending on the type of the input control.
 * - `errors` (optional): An array of error messages related to the input's
 *   validation. This property is optional and can be omitted if there are no errors.
 */
export type FormInputState = {
    value: string | boolean;
    errors?: string[];
    /**
     * Indicates that the current value corresponds to a disabled option (for select fields).
     */
    disabled?: boolean;
};

/**
 * Represents the state of form inputs, mapping input names to their respective {FormInputState}.
 * ```javascript
 * {
 *   "Vorname": {
 *     value: "Bob",
 *     errors: ["Vorname is required"]
 *   },
 *   "Nachname": {
 *     value: "Ross"
 *   }
 * }
 * ```
 * @see FormInputState
 */
export type FormInputs = Record<string, FormInputState>;

/**
 * Represents the state of a form in the application.
 *
 * Properties:
 * - `formInputs`: An object mapping input names to their respective {FormInputState}.
 * - `processState`: The current state of the form. It can be one of the following:
 * @see FormInputs
 * @see FormProcessState
 *
 */
export type FormState = {
    formInputs: FormInputs;
    processState: FormProcessState;
};

/**
 * The Storyblok components that make up the actual Form Inputs
 */
export type StoryBlokBasicFormConfig =
    | FormInputStoryblok
    | FormSelectStoryblok
    | FormRadioGroupStoryblok
    | FormRequiredGroupStoryblok
    | FormUploadStoryblok;

/**
 * Defines the configuration type for a StoryBlok form. It includes the configuration of the individual inputs
 * as well as Layout and other arbitrary components
 *
 * @see StoryBlokBasicFormConfig
 */
export type StoryBlokFormConfig = (
    | StoryBlokBasicFormConfig
    | GDoubleRowStoryblok
    | GSingleColumnStoryblok
    | GTripleRowStoryblok
)[];

/**
 * Enum representing various states of a form during its lifecycle.
 * DEFAULT: The form is in its initial state.
 * SUCCESS: The form has been submitted successfully.
 * ERROR: The form has been submitted with errors.
 * INVALID: The form configuration is invalid
 */
export enum FormProcessState {
    DEFAULT,
    SUCCESS,
    ERROR,
    INVALID
}

/**
 * Collects the input related configuration components from Storyblok form configuration.
 * It recursively processes nested form elements and extracts components of type "FormInput", "FormSelect",
 * and "FormRadioGroup".
 *
 * @param {StoryBlokFormConfig} bloks - The input form configuration from StoryBlok.
 * @returns {StoryBlokBasicFormConfig[]} - A flattened array containing filtered form components.
 */
export const collectInputConfig = (bloks: StoryBlokFormConfig): StoryBlokBasicFormConfig[] => {
    let inputBloks: StoryBlokFormConfig = [];

    bloks.forEach((component) => {
        if (
            component.component === 'FormInput' ||
            component.component === 'FormSelect' ||
            component.component === 'FormRadioGroup' ||
            component.component === 'FormUpload'
        ) {
            inputBloks.push(component);
        } else if (component.content) {
            const nested = collectInputConfig(component.content);
            inputBloks = [...inputBloks, ...nested];
        } else if (component.component === 'FormRequiredGroup') {
            const nested = collectInputConfig(component.formElements as StoryBlokFormConfig);
            inputBloks = [...inputBloks, component, ...nested];
        }
    });
    return inputBloks as StoryBlokBasicFormConfig[];
};

export const getInitialInputValue = (component: StoryBlokBasicFormConfig) => {
    if (component.Value) return component.Value;

    if (component.component === 'FormSelect') {
        return component.Options?.[0]?.Text;
    } else if (component.inputType === 'checkbox') {
        return false;
    } else {
        return '';
    }
};

/**
 * Constructs the initial state for a form by processing an array of form configuration blocks
 * and merging them with provided prefilled values.
 *
 * @param {StoryBlokBasicFormConfig[]} bloks - An array of form configuration objects
 * @param {FormInputs} prefilledValues - An object containing the prefilled values for the form inputs;
 * these values will be used when available.
 * @returns {FormInputs} - An object representing the initial state of the form,
 * where each key corresponds to an input key and its value contains input attributes like the default or prefilled value.
 */
export const constructFormState = (bloks: StoryBlokBasicFormConfig[], prefilledValues: FormInputs): FormInputs => {
    return bloks.reduce((stateObject, component: StoryBlokBasicFormConfig) => {
        const { inputKey } = component;
        // Initialize value from prefilled values or default
        const initialValue = prefilledValues[inputKey]?.value ?? getInitialInputValue(component);
        // Construct state, including disabled flag for select fields
        const formInput: FormInputState = {
            value: initialValue
        };
        if (component.component === 'FormSelect') {
            // Option data only available on select components
            const selectComp = component;
            const opt = selectComp.Options?.find((o) => o.Text === initialValue);
            if (opt?.disabled === true) {
                formInput.disabled = true;
            }
        }

        return {
            [inputKey]: formInput,
            ...stateObject
        };
    }, {});
};

export const collectPrefillValues = (params: URLSearchParams): FormInputs => {
    const prefillState: FormInputs = {};
    for (const [key, value] of params.entries()) {
        prefillState[key] = {
            value
        };
    }
    return prefillState;
};

export function getFormInput(formInputs: FormInputs | undefined, key: string): string | boolean | null {
    if (!formInputs) return null;

    const inputState = formInputs[key];
    if (!inputState) return null;

    return inputState.value;
}

export const validateFormConfig = (collectedFormData: StoryBlokFormConfig, formStateObject: FormInputs): boolean => {
    if (Object.keys(collectedFormData).length !== Object.keys(formStateObject).length) {
        console.error(
            `number of Input Key values and form elements do not match. Expected ${Object.keys(collectedFormData).length} but got ${Object.keys(formStateObject).length}.`
        );
        return false;
    }
    return true;
};
